// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasCartLinesWithErrors,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartEmpty,
    isPunchOutOrder
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import saveOrderApproval from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrderApproval";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import trackBeginCheckout from "@steiner/App/GoogleAnalyticsConnector/Handlers/Overrides/CheckoutShipping/TrackBeginCheckout";
import { xIsCartCheckoutDisabled } from "@steiner/App/Store/Selectors/CartsSelector";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@steiner/Overrides/Components/CartTotalDisplay";
import { goBack } from "@steiner/XDL/Utilities/goBack";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        accountSettings: { allowGuestCheckout },
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            // xng-change - STR-2674 - Allow checkout for zero price bid products
            xIsCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        allowGuestCheckout,
        isApprovalEditDraft: cart?.status === "AwaitingApprovalEditDraft",
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
        isSaveOrderApprovalButtonDisabled:
            isCartLoading ||
            isUpdatingCart ||
            isCartEmpty(cart) ||
            hasCartLinesWithErrors(cart, state.context.showInventory),
        // xng-change - STR-2567
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
    saveOrderApproval,
    // XNG-Changes
    trackBeginCheckout,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    // xng-change
    container?: InjectableCss;
    styledHr?: InjectableCss;
    styledHrGridItem?: GridItemProps;
    fullWidthGridItem?: GridItemProps;
    continueShoppingLinkWrapper?: InjectableCss;
    continueShoppingLink?: LinkPresentationProps;
    cartTotalContainer?: InjectableCss;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: fixed;
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            margin-top: 10px;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitRequisitionButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
};

const styles = cartTotalStyles;

// xng-change
const StyledHr = getStyledWrapper("hr");

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    allowGuestCheckout,
    isApprovalEditDraft,
    orderApprovalDetailsPageUrl,
    saveOrderApproval,
    isSaveOrderApprovalButtonDisabled,
    // xng-change - STR-2567
    homePageUrl,
    trackBeginCheckout,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe && allowGuestCheckout) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            // be sure to pass the right cartId to load the correct cart
            // XNG-Changes
            trackBeginCheckout({});
            history.push(
                isAwaitingApproval
                    ? `${checkoutReviewAndSubmitPageUrl}?cartId=${cartId}`
                    : checkoutReviewAndSubmitPageUrl!,
            );
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (!cart?.isAuthenticated) {
            setShowLoginModal(true);
            return;
        }

        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    /**
     * XNG-Changes
     * Update Label
     */
    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Bid") : translate("Request a Bid");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const saveOrderApprovalClickHandler = () => {
        if (!cart) {
            return;
        }

        saveOrderApproval({
            cart,
            onSuccess: () => {
                if (orderApprovalDetailsPageUrl) {
                    history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.orderNumber.split("_")[1]}`);
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.cartTotalContainer}>
            <CartTotalDisplay
                showTaxAndShipping={showTaxAndShipping}
                extendedStyles={styles.cartTotal}
                isLoading={isLoading}
                isCartEmpty={isCartEmpty}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
            >
                {fields.includeActions !== false && (
                    <>
                        <GridItem {...styles.styledHrGridItem}>
                            <StyledHr {...styles.styledHr} />
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Button
                                {...styles.checkoutButton}
                                onClick={checkoutHandler}
                                disabled={
                                    isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts || !canCheckoutWithCart
                                }
                                data-test-selector="cartTotal_checkout"
                            >
                                {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                            </Button>
                            {isPunchOutOrder && (
                                <Button
                                    {...styles.cancelPunchOutButton}
                                    disabled={isPunchoutButtonDisabled || !canCheckoutWithCart}
                                    onClick={cancelPunchOutHandler}
                                >
                                    {translate("Cancel PunchOut")}
                                </Button>
                            )}
                            <TwoButtonModal
                                headlineText=""
                                modalIsOpen={quoteRequiredModalIsOpen}
                                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                                cancelButtonText={translate("Cancel")}
                                submitButtonText={translate("Continue")}
                                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                                onSubmit={checkoutHandler}
                                submitTestSelector="QuoteInCartMessageSubmit"
                                dataTestSelector="twoButtonModal_RequiresQuote"
                            ></TwoButtonModal>
                        </GridItem>
                    </>
                )}
                {fields.includeActions !== false && canSubmitForQuote && (
                    <>
                        <GridItem {...styles.styledHrGridItem}>
                            <StyledHr {...styles.styledHr} />
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Button
                                {...styles.submitQuoteButton}
                                disabled={isSubmitForQuoteButtonDisabled || cart?.isAwaitingApproval}
                                onClick={submitForQuoteClickHandler}
                                data-test-selector="cartTotal_submitQuote"
                            >
                                {submitForQuoteLabel}
                            </Button>
                        </GridItem>
                    </>
                )}
                {fields.includeActions !== false && canSubmitRequisition && (
                    <>
                        <GridItem {...styles.styledHrGridItem}>
                            <StyledHr {...styles.styledHr} />
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Button
                                {...styles.submitRequisitionButton}
                                disabled={isSubmitRequisitionButtonDisabled}
                                onClick={submitRequisitionClickHandler}
                                data-test-selector="cartTotal_submitRequisition"
                            >
                                {translate("Submit Requisition")}
                            </Button>
                        </GridItem>
                    </>
                )}
                {fields.includeActions !== false && isApprovalEditDraft && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <Button
                            {...styles.checkoutButton}
                            disabled={isSaveOrderApprovalButtonDisabled}
                            onClick={saveOrderApprovalClickHandler}
                            data-test-selector="cartTotal_saveOrderApproval"
                        >
                            {translate("Save")}
                        </Button>
                    </GridItem>
                )}
                <Modal
                    sizeVariant="small"
                    headline={translate("Please sign in to Request A Bid")}
                    isOpen={showLoginModal}
                    handleClose={() => setShowLoginModal(false)}
                >
                    <StyledWrapper>
                        {createWidgetElement("SignIn/SignInExistingAccount", {
                            fields: {
                                isSigninModal: true,
                            },
                        })}
                    </StyledWrapper>
                </Modal>
            </CartTotalDisplay>
            <StyledWrapper {...styles.continueShoppingLinkWrapper}>
                <Link
                    {...styles.continueShoppingLink}
                    data-test-selector="cartTotalContinueShopping"
                    onClick={e => {
                        goBack(e, history, undefined);
                    }}
                >
                    {translate("Continue Shopping")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
